import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Button, Tooltip } from '@mui/material';
import { useEnqueueSnackbar } from 'hooks';
import type { PackEntry } from 'types/shared';
import {
  buildPackEntry,
  getHypothesisCreativesIdToHypothesis,
  groupIdsByHypothesis,
  removeCreativeIdsFromOwnedHypothesisPacks,
} from '../utils/hypothesisPackUtils';
import { pruneEmptyPacks } from './creatives/applyPackCreativesMove';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';

const AddHypothesisPackButton = () => {
  const { onShowAlert } = useEnqueueSnackbar();
  const { watch, setValue, trigger } = useFormContext();
  const selectedCreatives = watch('selectedCreatives') as string[];
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const testCreativesTab = watch('testCreativesTab') as string | undefined;

  const isAddPackAvailable =
    testCreativesTab === 'test_creatives' && selectedCreatives.length > 0;

  const handleAddPack = useCallback(() => {
    const idToHypothesis = getHypothesisCreativesIdToHypothesis();
    const groupedByHypothesis = groupIdsByHypothesis(
      selectedCreatives,
      idToHypothesis,
    );

    const newPacks: PackEntry[] = [...groupedByHypothesis.entries()].map(
      ([hypothesisLabel, creatives]) => buildPackEntry(hypothesisLabel, creatives),
    );

    if (!newPacks.length) return;

    const packsWithMovedCreatives = removeCreativeIdsFromOwnedHypothesisPacks(
      packsWithCreatives,
      selectedCreatives,
      idToHypothesis,
    );
    const { packs: prunedExistingPacks } = pruneEmptyPacks(packsWithMovedCreatives);
    const { packs: updatedPacks } = pruneEmptyPacks([
      ...prunedExistingPacks,
      ...newPacks,
    ]);
    setValue('packsWithCreatives', updatedPacks, { shouldValidate: true });
    setValue('activePacksWithCreatives', prunedExistingPacks.length);
    setValue('selectedCreatives', [], { shouldValidate: true });

    onShowAlert(
      newPacks.length === 1 ? 'Pack added' : `${newPacks.length} packs added`,
      { delayAlert: 3500 },
    );
    datadogActions.trackDatadogAction(DATADOG_EVENT.facebookPackAdded, {
      createdPacksCount: newPacks.length,
      selectedCreativesCount: selectedCreatives.length,
    });

    void trigger('packsWithCreatives');
    void trigger('selectedCreatives');
  }, [
    selectedCreatives,
    packsWithCreatives,
    setValue,
    trigger,
    onShowAlert,
  ]);

  return (
    <Tooltip title={'Select creatives to add a new pack'} placement={'top'}>
      <Box>
        <Button
          disabled={!isAddPackAvailable}
          variant="outlined"
          color="primary"
          onClick={handleAddPack}
        >
          Add pack
        </Button>
      </Box>
    </Tooltip>
  );
};

export default AddHypothesisPackButton;
